import React from 'react'
import { useTranslation } from 'react-i18next'
import camelCase from 'camelcase'

import { FieldProps } from '../../../../../../../@types/types'
import { BasicInfoScreenValidationResult } from '../../../../../../../@types/validation-types'
import { NumberInput } from '../../../../../../common/inputs/numberInput/NumberInput'
import { DefaultRentPriceTypeOption } from '../../../../../../../@types/dropdown-types'
import { RealEstatePriceTypeOption } from '../../../../../../../@types/real-estate-api-types'
import { useOnPriceChange } from './useOnPriceChanged'
import LabelWithTooltip from '../common/LabelWithTooltip'
import PopoverContent from '../../../objectDetails/fields/common/PopoverContent'

export enum PriceFieldType {
  COLD_RENT = 'COLD_RENT',
  RENT_PRICE = 'RENT_PRICE',
  YEARLY_RENT_PRICE = 'YEARLY_RENT_PRICE',
  BUY_PRICE = 'BUY_PRICE',
  FLAT_RATE_RENT = 'FLAT_RATE_RENT',
}

export type PriceFieldProps = {
  priceFieldType: PriceFieldType
  showLabelWithTooltip?: boolean
  tooltipTranslationKey?: string
}

const PriceField: React.FC<FieldProps<BasicInfoScreenValidationResult> & PriceFieldProps> = ({
  screenName,
  insertionData,
  priceFieldType,
  validationMessages,
  wrapperClassName = 'one-half padding-right-m',
  showLabelWithTooltip = false,
  tooltipTranslationKey = 'wizard.screen.costs.baseRentHint',
}) => {
  const { t } = useTranslation()
  const onPriceChange = useOnPriceChange()

  const labelWithTooltip = (
    <LabelWithTooltip
      inputId='price'
      labelText={t(`wizard.screen.basicInfo.price.${camelCase(priceFieldType)}`)}
      tooltipContent={<PopoverContent translationKey={tooltipTranslationKey} />}
      showMandatory={true}
    />
  )

  const labelWithoutTooltip = <span>{t(`wizard.screen.basicInfo.price.${camelCase(priceFieldType)}`, 'Price*')}</span>

  return (
    <div className={`grid-item ${wrapperClassName}`}>
      {showLabelWithTooltip && labelWithTooltip}
      <NumberInput
        id='price'
        propertyName='price'
        placeholder=''
        label={showLabelWithTooltip ? '' : labelWithoutTooltip}
        value={insertionData.price}
        minPrecision={0}
        maxPrecision={2}
        maxLength={14}
        affixRight='unit'
        unitRight='eur'
        errors={validationMessages!.price ?? []}
        disabled={
          insertionData.priceType?.value === DefaultRentPriceTypeOption.REQUEST_RENT_PRICE ||
          insertionData.priceType?.value === RealEstatePriceTypeOption.REQUEST_PURCHASE_PRICE
        }
        doOnFieldChange={(property, value) => {
          onPriceChange({ screen: screenName, property }, value)
        }}
        hideMandatorySign={showLabelWithTooltip}
        dataHeyimmoContext='price_monthly_rent'
      />
    </div>
  )
}
export default PriceField
